// ModeSelector.tsx
import { useRef, useState, useEffect } from 'react';
import { Truck, Droplets, Gauge, ChevronDown, LayoutGrid } from 'lucide-react';
import type { ModeId } from './ModeLanding';

const MODE_META: Record<ModeId, { label: string; icon: React.ReactNode }> = {
    crude: { label: 'Crude Transport', icon: <Truck size={14} /> },
    vac:   { label: 'Vac Truck',       icon: <Droplets size={14} /> },
    ngl:   { label: 'NGL',             icon: <Gauge size={14} /> },
};

interface ModeSelectorProps {
    activeMode: ModeId;
    onChangeMode: (mode: ModeId) => void;
    onBackToOverview: () => void;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({ activeMode, onChangeMode, onBackToOverview }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const current = MODE_META[activeMode];

    return (
        <div className="modeSelector-wrapper" ref={ref}>
            <button className="modeSelector-trigger" onClick={() => setOpen(o => !o)}>
                {current.icon}
                <span className="modeSelector-label">{current.label}</span>
                <ChevronDown size={14} className={`modeSelector-chevron ${open ? 'modeSelector-chevron--open' : ''}`} />
            </button>

            {open && (
                <div className="modeSelector-dropdown">
                    {(Object.entries(MODE_META) as [ModeId, typeof MODE_META[ModeId]][]).map(([id, meta]) => (
                        <button
                            key={id}
                            className={`modeSelector-option ${id === activeMode ? 'modeSelector-option--active' : ''}`}
                            onClick={() => {
                                onChangeMode(id);
                                setOpen(false);
                            }}
                        >
                            {meta.icon}
                            <span>{meta.label}</span>
                            {id === activeMode && <span className="modeSelector-check">✓</span>}
                        </button>
                    ))}

                    <div className="modeSelector-divider" />

                    <button
                        className="modeSelector-option modeSelector-option--back"
                        onClick={() => { onBackToOverview(); setOpen(false); }}
                    >
                        <LayoutGrid size={14} />
                        <span>Back to Overview</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default ModeSelector;
